import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search,
  AlertTriangle,
  FileText,
  ShieldCheck,
  Send,
  Loader2,
  ArrowLeft,
} from 'lucide-react';
import { slugify } from '../utils/slugify';
import {
  analyzeContract,
  getContractReport,
  startContractChat,
  askContractQuestion,
  getContractChatHistory,
} from '../../api/api';
import { fetchChatSessions } from '../../redux/slices/chatSessionsSlice';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import PlanLimitReachedModal from '../../views/PlanLimitReachedModal';
import UpgradeModal from '../usage/UpgradeModal';
import ScaleLogo from '../../assets/scale-balance.svg';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { useDispatch } from 'react-redux';
import { storeContractReport } from '../../redux/slices/reportSlice';
import { fetchUserStatistics } from '../../redux/slices/userStatisticsSlice';
import { handleApiError } from '../../helpers/handleApiError';

export default function ActionLoader({ isDarkMode }) {
  const actions = [
    {
      title: 'Full Contract Analysis',
      analysisType: 'full',
      icon: (
        <Search
          className={`h-5 w-5 ${isDarkMode ? 'text-white' : 'text-black'}`}
        />
      ),
    },
    {
      title: 'Risk Analysis',
      analysisType: 'risk',
      icon: (
        <AlertTriangle
          className={`h-5 w-5 ${isDarkMode ? 'text-white' : 'text-black'}`}
        />
      ),
    },
    {
      title: 'Extract Key Clauses',
      analysisType: 'clauses',
      icon: (
        <FileText
          className={`h-5 w-5 ${isDarkMode ? 'text-white' : 'text-black'}`}
        />
      ),
    },
    {
      title: 'Compliance Check',
      analysisType: 'compliance',
      icon: (
        <ShieldCheck
          className={`h-5 w-5 ${isDarkMode ? 'text-white' : 'text-black'}`}
        />
      ),
    },
    {
      title: 'Question & Answer',
      analysisType: 'qa',
      icon: (
        <Search
          className={`h-5 w-5 ${isDarkMode ? 'text-white' : 'text-black'}`}
        />
      ),
    },
  ];

  const user = useSelector((state) => state.auth.user);
  const uid = user?.id;
  const { type } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const messagesEndRef = useRef(null);
  const dispatch = useDispatch();
  const [fileName, setFileName] = useState('');
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [questionLoading, setQuestionLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const [qaSessionId, setQaSessionId] = useState(null);
  const queryParams = new URLSearchParams(location.search);
  const sessionIdFromUrl = queryParams.get('sessionId');
  const currentAction = actions.find((a) => slugify(a.title) === type);
  const remainingActions = actions.filter((a) => a !== currentAction);
  const reduxFileName = useSelector((state) => state.document.selectedFileName);
  const isQA = currentAction?.analysisType === 'qa';
  const initialSessionId = useRef(sessionIdFromUrl || null);
  const stats = useSelector((state) => state.userStatistics.stats);
  const remainingChatQueries =
    stats?.usage_month_to_date?.chatbot_queries?.value;
  const remainingDocumentAnalysis =
    stats?.usage_month_to_date?.document_analysis?.value;
  const activeOrder = user?.subscription || null;

  const getReport = async (fileName) => {
    try {
      const res = await getContractReport(uid, fileName);
      return res?.report || null;
    } catch (err) {
      handleApiError(err, 'Failed to fetch report');

      return null;
    }
  };
  const loadHistory = () => {
    setHistoryLoading(true);

    getContractChatHistory(uid, qaSessionId)
      .then((res) => {
        if (res?.messages?.length) {
          const history = res.messages.map((m) => ({
            role: m.role?.toLowerCase() || (m.uid === 0 ? 'user' : 'bot'),
            content: m.content || m.text || '',
          }));
          setMessages(history);
        }
        console.log('res', res);

        if (res?.chat_session) {
          setFileName(res?.chat_session?.contract?.filename);
        }
      })
      .catch((err) => {
        handleApiError(err, 'Failed to load chat history');
      })
      .finally(() => {
        setHistoryLoading(false);
      });
  };

  // Always hydrate fileName from Redux or location.state
  useEffect(() => {
    const selectedFileName = location?.state?.fileName || reduxFileName;
    if (selectedFileName && selectedFileName !== fileName) {
      setFileName(selectedFileName);
    }
  }, [reduxFileName, location.state?.fileName]);

  // 🔥 Reload history anytime sessionId changes (even after mount)
  useEffect(() => {
    if (!qaSessionId) return;

    // ✅ Case 1: Page refresh — sessionId came from URL
    if (initialSessionId.current === qaSessionId) {
      console.log('📜 Loading history on refresh:', qaSessionId);
      loadHistory();
      return;
    }

    // 🚫 Case 2: First-time session creation — skip loading history
    if (!initialSessionId.current) {
      console.log('⏭️ Skipping history for new session:', qaSessionId);
      initialSessionId.current = qaSessionId; // Next time treat it normally
      return;
    }

    loadHistory();
  }, [qaSessionId]);

  // ✅ Session lifecycle: always respond to the current route and its query
  useEffect(() => {
    // ✅ Only update if URL sessionId differs from current state
    if (sessionIdFromUrl && sessionIdFromUrl !== qaSessionId) {
      console.log('🔁 Syncing session from URL:', sessionIdFromUrl);
      setQaSessionId(sessionIdFromUrl);
    }

    // ✅ Clear session only when URL is clean
    if (!sessionIdFromUrl && qaSessionId) {
      console.log('🧹 Clearing old session...');
      setQaSessionId(null);
      setMessages([]);
    }

    // ❌ Do NOT depend on qaSessionId here, or it will trigger twice
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, sessionIdFromUrl]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const selectedFileName = location.state?.fileName || reduxFileName;
    const selectedAnalysisType = location.state?.analysisType;
    if (!selectedFileName || !selectedAnalysisType) return;

    setFileName(selectedFileName);
    setMessages([]);
    setQaSessionId(null);
    // ⛔️ Skip initial analysis if it's "qa"
    if (selectedAnalysisType === 'qa') return;
    // ⛔ Document analysis limit check
    if (remainingDocumentAnalysis === 0) {
      setShowLimitModal(true);
      return;
    }
    setAnalysisLoading(true);

    analyzeContract(uid, selectedAnalysisType, selectedFileName, input, signal)
      .then((res) => {
        if (!res) return;
        // ⬇️ Capture session from analysis API
        if (res?.chat_session_id) {
          setQaSessionId(res.chat_session_id);

          // Sync to URL so QA can continue later
          navigate(
            `/app/document/action/${type}?sessionId=${res.chat_session_id}`,
            { replace: true },
          );
        }

        let content;
        if (res?.results && typeof res.results === 'object') {
          content = res.results;
        } else {
          content =
            typeof res.results === 'string'
              ? res.results
              : JSON.stringify(res, null, 2);
        }

        setMessages([{ role: 'bot', content }]);

        dispatch(fetchChatSessions());
        dispatch(fetchUserStatistics(uid));

        // toast.success('Analysis complete!', { id: 'analysis' });

        // 🔹 Fetch report AFTER analysis completes
        return getReport(selectedFileName);
      })
      .then((report) => {
        if (report) {
          dispatch(storeContractReport(report));
        }
      })
      .catch((err) => {
        if (err.name !== 'CanceledError') {
          console.error(err);
          console.log('error in doc check', err);

          toast.error(
            err?.response.data.detail ||
              err?.response.data.error ||
              'Failed to analyze document',
            { id: 'analysis' },
          );
        }
      })
      .finally(() => {
        setAnalysisLoading(false);
      });

    return () => {
      controller.abort();
      toast.dismiss();
    };
  }, [
    type,
    reduxFileName,
    location.state?.fileName,
    location.state?.analysisType,
  ]);

  // 🔹 Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, analysisLoading, questionLoading]);

  const handleSend = async () => {
    if (isBusy) return;

    if (!input.trim()) return;

    // ⛔ Limit handling for both QA and follow-up questions
    if (remainingChatQueries === 0) {
      setShowLimitModal(true);
      return;
    }
    if (activeOrder?.status === 'paused') {
      toast.error(
        'Your subscription is paused. Please resume your subscription to continue.',
      );
      return;
    }

    const question = input;
    setMessages((prev) => [...prev, { role: 'user', content: question }]);
    setInput('');
    setQuestionLoading(true);

    try {
      let content = '';

      // -------------------------------------------------------
      // 1️⃣ NON-QA FOLLOWUPS → use askContractQuestion
      // -------------------------------------------------------
      if (!isQA) {
        if (!qaSessionId) {
          toast.error('Session not initialized.');
          return;
        }

        const res = await askContractQuestion(
          uid,
          qaSessionId,
          fileName,
          question,
          // currentAction?.analysisType
        );

        // ⬇️ NEW — refresh sessions after follow-up
        setTimeout(() => dispatch(fetchChatSessions()), 300);
        dispatch(fetchUserStatistics(uid));
        if (res?.results?.qa_response) {
          content = res.results.qa_response;
        } else if (res?.results && typeof res.results === 'object') {
          content = Object.values(res.results)
            .filter((val) => typeof val === 'string')
            .join('\n\n');
        } else {
          content = '```json\n' + JSON.stringify(res, null, 2) + '\n```';
        }

        setMessages((prev) => [...prev, { role: 'bot', content }]);
        return;
      }

      // -------------------------------------------------------
      // 2️⃣ QA MODE → chat flow (create session if needed)
      // -------------------------------------------------------
      let sessionId = qaSessionId;

      if (!sessionId) {
        const sessionRes = await startContractChat(
          uid,
          fileName,
          'Document Q&A',
        );

        sessionId = sessionRes?.chat_session_id;
        if (!sessionId) throw new Error("Couldn't start chat session");

        setQaSessionId(sessionId);
        navigate(`/app/document/action/${type}?sessionId=${sessionId}`, {
          replace: true,
        });

        dispatch(fetchChatSessions());
      }

      const res = await askContractQuestion(
        uid,
        sessionId,
        fileName,
        question,
        // currentAction?.analysisType
      );

      // ⬇️ NEW — refresh sessions after Q&A question
      dispatch(fetchChatSessions());
      dispatch(fetchUserStatistics(uid));

      if (res?.results?.qa_response) {
        content = res.results.qa_response;
      } else if (typeof res?.results === 'object') {
        content = Object.values(res.results)
          .filter((v) => typeof v === 'string')
          .join('\n\n');
      } else {
        content = '```json\n' + JSON.stringify(res, null, 2) + '\n```';
      }

      setMessages((prev) => [...prev, { role: 'bot', content }]);
    } catch (err) {
      console.error(err);
      toast.error('Failed to get answer');
    } finally {
      setQuestionLoading(false);
    }
  };

  if (!currentAction) {
    return (
      <div className="relative w-full h-[calc(100vh-80px)] flex flex-col items-center justify-center text-center">
        {/* Top-Left Back Button */}
        <button
          onClick={() => navigate('/app/document/action')}
          className={`absolute top-4 left-4 group inline-flex items-center gap-2 px-3 py-2 rounded-md border transition-all duration-200 shadow-sm
          ${
            isDarkMode
              ? 'bg-gray-800 text-indigo-300 border-gray-700 hover:bg-gray-700 hover:text-white'
              : 'bg-brand text-white'
          }`}
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
        </button>

        {/* Center Content */}
        <div className="flex flex-col items-center gap-2">
          {/* ⚠️ Error Icon */}
          <AlertTriangle className="w-10 h-10 text-red-500" />

          <h2 className="text-2xl font-bold text-brand">Action not found</h2>
          <p className="text-gray-500">
            The action you're looking for doesn't exist.
          </p>
        </div>
      </div>
    );
  }

  const formatMarkdownList = (text) => {
    return text
      .replaceAll('\\n', '\n')
      .split('\n')
      .map((line) => {
        if (line.trim().startsWith('•')) {
          return `- ${line.replace('•', '').trim()}`;
        }
        return line.trim();
      })
      .join('\n');
  };
  const isBusy = analysisLoading || questionLoading || historyLoading;
  const handleActionClick = (action) => {
    if (isBusy) return;

    // Document analysis limit check
    if (action.analysisType !== 'qa' && remainingDocumentAnalysis === 0) {
      setShowLimitModal(true);
      return;
    }

    // QA limit check
    if (action.analysisType === 'qa' && remainingChatQueries === 0) {
      setShowLimitModal(true);
      return;
    }

    navigate(`/app/document/action/${slugify(action.title)}`, {
      state: { fileName, analysisType: action.analysisType },
      replace: true,
    });
  };

  return (
    <div
      className={`w-full flex flex-col px-3 py-3 ${
        isDarkMode ? 'bg-[#0e0e11]' : 'bg-white'
      }`}
    >
      {/* Back Button */}
      {!sessionIdFromUrl && (
        <div className="mb-2">
          <button
            onClick={() => navigate('/app/document/action')}
            className={`group inline-flex items-center gap-2 px-3 py-2 rounded-md border transition-all duration-200 shadow-sm
        ${
          isDarkMode
            ? 'bg-gray-800 text-indigo-300 border-gray-700 hover:bg-gray-700 hover:text-white'
            : 'bg-brand text-white'
        }`}
          >
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          </button>
        </div>
      )}

      {/* Title */}
      <h2
        className={`text-2xl font-semibold ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}
      >
        {fileName ? (
          fileName
        ) : (
          <div
            className={`h-6 w-40 rounded-md animate-pulse ${
              isDarkMode ? 'bg-gray-700' : 'bg-gray-300'
            }`}
          ></div>
        )}
      </h2>

      {!isQA && (
        <div className="flex flex-wrap justify-center gap-2 max-w-4xl mx-auto">
          {remainingActions.map((action, index) => (
            <button
              key={index}
              disabled={isBusy}
              onClick={() => handleActionClick(action)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition shadow-sm
    ${isBusy ? 'opacity-50 cursor-not-allowed' : ''}
    ${
      isDarkMode
        ? 'bg-gray-800 hover:bg-gray-700 text-white'
        : 'bg-[#F4F4FF] hover:bg-[#E8E8FC] text-gray-800'
    }
  `}
            >
              {action.icon}
              {action.title}
            </button>
          ))}
        </div>
      )}

      {!isQA && messages.length === 0 && analysisLoading && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`flex items-center gap-3 text-base font-medium px-4 py-3 my-2 rounded-lg max-w-xl mx-auto mb-4
            ${
              isDarkMode
                ? 'text-indigo-300 bg-gray-800'
                : 'text-indigo-600 bg-[#F4F4FF]'
            }`}
        >
          <motion.span
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 1, repeat: Infinity }}
          >
            {currentAction.icon}
          </motion.span>
          <span>Generating {currentAction.title}...</span>
        </motion.div>
      )}

      <div className="flex-1 overflow-y-auto mt-4 space-y-3 pr-1 relative max-w-3xl mx-auto">
        {historyLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-transparent z-10">
            <Loader2 className="w-10 h-10 animate-spin text-brand" />
          </div>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: historyLoading ? 0 : 1 }}
          transition={{ duration: 0.3 }}
          style={{
            pointerEvents: historyLoading ? 'none' : 'auto',
            filter: historyLoading ? 'blur(2px)' : 'none',
          }}
        >
          {messages.map((msg, idx) => {
            const isUser = msg.role === 'user';
            const isStructured = !isUser && typeof msg.content === 'object';

            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: idx * 0.05 }}
                className={`flex w-full mb-3 ${
                  isUser ? 'justify-end' : 'justify-start'
                }`}
              >
                {isStructured ? (
                  <div className="space-y-4 w-full">
                    {Object.entries(msg.content).map(([key, value]) => {
                      let title = '';
                      let icon = null;

                      switch (key) {
                        case 'compliance_check':
                          title = 'Compliance Check';
                          icon = <ShieldCheck className="h-5 w-5 text-brand" />;
                          break;
                        case 'key_clauses':
                          title = 'Key Clauses';
                          icon = <FileText className="h-5 w-5 text-brand" />;
                          break;
                        case 'risk_analysis':
                          title = 'Risk Analysis';
                          icon = (
                            <AlertTriangle className="h-5 w-5 text-brand" />
                          );
                          break;
                        case 'qa_response':
                          title = 'Questions and Answers';
                          icon = (
                            <AlertTriangle className="h-5 w-5 text-brand" />
                          );
                          break;
                        default:
                          title = key;
                      }

                      return (
                        <motion.div
                          key={key}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                          className={`p-4 rounded-lg shadow border w-full ${
                            isDarkMode
                              ? 'bg-gray-800 border-gray-700 text-gray-200'
                              : 'bg-white border-gray-200 text-gray-800'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-3">
                            {icon}
                            <h3 className="font-semibold">{title}</h3>
                          </div>
                          <div className="text-sm">
                            <ReactMarkdown
                              rehypePlugins={[rehypeRaw]}
                              components={{
                                h1: ({ children, ...props }) => (
                                  <h1
                                    className="text-2xl font-bold text-brand mb-2"
                                    {...props}
                                  >
                                    {children}
                                  </h1>
                                ),
                                h2: ({ children, ...props }) => (
                                  <h2
                                    className="text-xl font-semibold text-brand mb-2"
                                    {...props}
                                  >
                                    {children}
                                  </h2>
                                ),
                                h3: ({ children, ...props }) => (
                                  <h3
                                    className="text-lg font-medium text-brand mb-1"
                                    {...props}
                                  >
                                    {children}
                                  </h3>
                                ),

                                strong: ({ children, ...props }) => (
                                  <strong
                                    className="font-bold text-brand"
                                    {...props}
                                  >
                                    {children}
                                  </strong>
                                ),

                                em: ({ children, ...props }) => (
                                  <em
                                    className="italic text-gray-500"
                                    {...props}
                                  >
                                    {children}
                                  </em>
                                ),

                                blockquote: ({ children, ...props }) => (
                                  <blockquote
                                    className="border-l-4 pl-4 italic text-gray-600 border-gray-400"
                                    {...props}
                                  >
                                    {children}
                                  </blockquote>
                                ),

                                li: ({ children, ...props }) => (
                                  <li className="mb-1" {...props}>
                                    {children}
                                  </li>
                                ),

                                p: ({ children, ...props }) => (
                                  <p
                                    className="text-sm leading-relaxed mb-2"
                                    {...props}
                                  >
                                    {children}
                                  </p>
                                ),
                              }}
                            >
                              {formatMarkdownList(
                                String(value).replaceAll('\\n', '\n'),
                              )}
                            </ReactMarkdown>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                ) : (
                  /* Regular messages */
                  <div
                    className={`p-3 rounded-lg text-sm shadow ${
                      isUser ? 'max-w-[75%] w-fit' : 'w-full'
                    } ${
                      isUser
                        ? isDarkMode
                          ? 'bg-indigo-900 text-indigo-200'
                          : 'bg-indigo-100 text-indigo-800'
                        : isDarkMode
                          ? 'bg-gray-700 text-gray-200'
                          : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    <ReactMarkdown
                      rehypePlugins={[rehypeRaw]}
                      components={{
                        strong: ({ children, ...props }) => (
                          <strong className="font-bold text-brand" {...props}>
                            {children}
                          </strong>
                        ),

                        h1: ({ children, ...props }) => (
                          <h1
                            className="text-2xl font-bold text-brand mb-2"
                            {...props}
                          >
                            {children}
                          </h1>
                        ),
                        h2: ({ children, ...props }) => (
                          <h2
                            className="text-xl font-semibold text-brand mb-2"
                            {...props}
                          >
                            {children}
                          </h2>
                        ),
                        h3: ({ children, ...props }) => (
                          <h3
                            className="text-lg font-medium text-brand mb-1"
                            {...props}
                          >
                            {children}
                          </h3>
                        ),

                        li: ({ children, ...props }) => (
                          <li className="mb-1" {...props}>
                            {children}
                          </li>
                        ),
                      }}
                    >
                      {String(msg.content).replaceAll('\\n', '\n')}
                    </ReactMarkdown>
                  </div>
                )}
              </motion.div>
            );
          })}

          {questionLoading && messages.some((m) => m.role === 'user') && (
            <div className="flex justify-start">
              <div
                className={`relative max-w-[85%] px-3 py-1.5 ${
                  isDarkMode ? 'bg-gray-700' : 'bg-white'
                }`}
              >
                <div className="flex items-center mb-1">
                  {/* Avatar */}
                  <div
                    className={`p-1 rounded-full shadow-sm w-6 h-6 flex items-center justify-center
          ${isDarkMode ? 'bg-gray-600' : 'bg-brandLighter'}`}
                  >
                    <img src={ScaleLogo} alt="Scale" className="w-4 h-4" />
                  </div>

                  {/* Thinking text + dots */}
                  <div className="flex items-center space-x-2 ml-2">
                    <span
                      className={`text-sm ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}
                    >
                      Thinking
                    </span>

                    <div className="flex space-x-1.5 py-0.5">
                      <div
                        className={`h-1.5 w-1.5 rounded-full animate-bounce ${
                          isDarkMode ? 'bg-brand' : 'bg-brand'
                        }`}
                      ></div>
                      <div
                        className={`h-1.5 w-1.5 rounded-full animate-bounce ${
                          isDarkMode ? 'bg-brand' : 'bg-brand'
                        }`}
                        style={{ animationDelay: '0.2s' }}
                      ></div>
                      <div
                        className={`h-1.5 w-1.5 rounded-full animate-bounce ${
                          isDarkMode ? 'bg-brand' : 'bg-brand'
                        }`}
                        style={{ animationDelay: '0.4s' }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </motion.div>
      </div>

      {/* Input */}
      <div className="sticky bottom-0 bg-inherit pt-4  px-4">
        <div className="w-full max-w-3xl mx-auto">
          <div
            className={`flex items-center border rounded-xl px-4 py-3 shadow-sm
            ${
              isDarkMode
                ? 'border-gray-700 bg-gray-800'
                : 'border-gray-200 bg-white'
            }`}
          >
            <input
              type="text"
              disabled={isBusy}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder={
                isQA
                  ? 'Ask a Question about the Document'
                  : 'Ask a follow up Document Question'
              }
              className={`flex-grow text-sm outline-none placeholder-gray-400
                ${
                  isDarkMode ? 'text-gray-100 bg-transparent' : 'text-gray-800'
                }`}
            />
            <button
              onClick={handleSend}
              disabled={analysisLoading || questionLoading || !input.trim()}
              className={`ml-3 px-4 py-2 rounded-lg transition-all duration-200 
  flex items-center gap-2 text-sm font-medium
  ${
    analysisLoading || questionLoading || !input.trim()
      ? isDarkMode
        ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
      : isDarkMode
        ? 'bg-brand text-white hover:opacity-90 active:scale-95'
        : 'bg-brand text-white hover:bg-brandDark active:scale-95'
  }`}
            >
              {questionLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Send</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <PlanLimitReachedModal
        open={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        activeOrder={activeOrder}
        onUpgrade={() => {
          setShowLimitModal(false);
          setShowUpgradeModal(true);
        }}
      />

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        isDarkMode={isDarkMode}
      />
    </div>
  );
}
